/**
 * CreatorAccordion which inherits from BaseAccordion
 * The main differences between all inherited accordions is applied styling and the header rendering.The main differences between all inherited accordions is the styling and the header rendering
 * This is used for "add new user", meant to be nested inside other accordions
 */

import React, { forwardRef, ReactNode, useEffect, useState } from 'react'

import BaseAccordion from './BaseAccordion'
import CustomIcons from './CustomIcons'
import { HeaderLarge500Style } from '../../styles/Text.styles'

import {
  AccordionContainer,
  PanelContainer,
  DynamicHeader,
  IconContainer,
  PanelContent,
} from './styles/CreatorAccordion.styles'

interface CreatorAccordionProps {
  children: ReactNode
  onContextReady?: () => void
  id: string
  defaultActiveKey?: string | string[] | null
  contextIdGenerated?: (id: string) => void
}

interface CreatorPanelProps {
  header?: string | ReactNode
  children?: ReactNode
  eventKey?: string
  noCollapse?: boolean
}

interface CreatorPanelRef {
  tryCollapse: () => void
}

const CreatorAccordion: React.FC<CreatorAccordionProps> = ({
  children,
  onContextReady,
  ...props
}) => {
  return (
    <AccordionContainer>
      <BaseAccordion {...props}>{children}</BaseAccordion>
    </AccordionContainer>
  )
}

/**
 * Tracks accordion open state so we can lazy-mount heavy form content
 * (avoids firing list API hooks while the panel is collapsed).
 */
const PanelHeader: React.FC<{
  header?: string | ReactNode
  isActive: boolean
  onToggle: () => void
  onActivated: () => void
}> = ({ header, isActive, onToggle, onActivated }) => {
  useEffect(() => {
    if (isActive) onActivated()
  }, [isActive, onActivated])

  return (
    <DynamicHeader isActive={isActive} onClick={onToggle}>
      <span css={HeaderLarge500Style}>{header}</span>
      <IconContainer isActive={isActive}>
        {isActive ? (
          <CustomIcons.MinusCircleIcon />
        ) : (
          <CustomIcons.PlusCircleIcon />
        )}
      </IconContainer>
    </DynamicHeader>
  )
}

/**
 * Panel subcomponent for CreatorAccordion
 * Renders a header with text and animated plus icon
 * Content is wrapped in PanelContent for consistent spacing
 *
 * Children mount only after the panel is opened at least once.
 */
const Panel = forwardRef<CreatorPanelRef, CreatorPanelProps>(
  ({ header, children, ...props }, ref) => {
    const [hasOpened, setHasOpened] = useState(false)

    return (
      <PanelContainer>
        <BaseAccordion.Panel
          {...props}
          ref={ref}
          renderHeader={({ isActive, onToggle }) => (
            <PanelHeader
              header={header}
              isActive={isActive}
              onToggle={onToggle}
              onActivated={() => setHasOpened(true)}
            />
          )}
        >
          <PanelContent>{hasOpened ? children : null}</PanelContent>
        </BaseAccordion.Panel>
      </PanelContainer>
    )
  },
)

Panel.displayName = 'CreatorPanel'

// Create the compound component
const CreatorAccordionCompound = CreatorAccordion as typeof CreatorAccordion & {
  Panel: typeof Panel
}

CreatorAccordionCompound.Panel = Panel

export default CreatorAccordionCompound
